/* eslint-disable react-hooks/exhaustive-deps */
//
// This hook orchestrates the live WebSocket chat session and has four
// intentional violations of exhaustive-deps:
//
//  - handleWebSocketMessage (useCallback) wraps a factory call. Its deps are
//    hand-curated to match the values the factory closes over via its `deps`
//    object literal; ESLint can't statically infer those.
//  - connectWithRetry (useCallback) intentionally captures handleWebSocketMessage,
//    handleConnectionFailure, sendMessage, and urlChatId via closure. Adding them
//    as deps would re-create the function on every WebSocket message and tear
//    down the active connection.
//  - handleConnectionFailure (useCallback) intentionally captures connectWithRetry
//    via closure to avoid a definition cycle.
//  - The mount-init useEffect intentionally depends only on startTrigger (the
//    externally-sourced-session confirmation gate re-arms it exactly once).
//    Including agentMode, connectWithRetry, urlChatId, etc. would cause a full
//    re-initialization (state reset + WebSocket reconnect) on every render where
//    any of those changes.
//
// Other hooks in this file are still checked by other rules. If you add a new
// hook here, write it with proper exhaustive-deps satisfied, or add a per-line
// disable with explicit reasoning. Do NOT extend the file-level disable lightly.
import { WebSocket } from 'partysocket';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
    RateLimitExceededError,
	type BlueprintType,
	type WebSocketMessage,
	type CodeFixEdits,
	type ImageAttachment,
	type PresenceMember,
	type AttachmentRef
} from '@/api-types';
import { ApiError } from '@/lib/api-client';
import {
	createRepairingJSONParser,
	ndjsonStream,
} from '@/utils/ndjson-parser/ndjson-parser';
import { getFileType } from '@/utils/string';
import { logger } from '@/utils/logger';
import { apiClient } from '@/lib/api-client';
import { appEvents } from '@/lib/app-events';
import { createWebSocketMessageHandler, type HandleMessageDeps, type ImageGenerationState } from '../utils/handle-websocket-message';
import { isConversationalMessage, addOrUpdateMessage, createUserMessage, handleRateLimitError, createAIMessage, type ChatMessage } from '../utils/message-helpers';
import { sendWebSocketMessage } from '../utils/websocket-helpers';
import { initialStages as defaultStages, updateStage as updateStageHelper } from '../utils/project-stage-helpers';
import type { ProjectStage } from '../utils/project-stage-helpers';
import type { TakeoverRequest, TakeoverStatus } from '../utils/takeover';


export interface FileType {
	filePath: string;
	fileContents: string;
	explanation?: string;
	isGenerating?: boolean;
	needsFixing?: boolean;
	hasErrors?: boolean;
	language?: string;
}

// New interface for phase timeline tracking
export interface PhaseTimelineItem {
	id: string;
	name: string;
	description: string;
	files: {
		path: string;
		purpose: string;
		status: 'generating' | 'completed' | 'error' | 'validating';
		contents?: string;
	}[];
	status: 'generating' | 'completed' | 'error' | 'validating';
	timestamp: number;
}

export function useChat({
	chatId: urlChatId,
	query: userQuery,
	images: userImages,
	attachments: userAttachments,
	agentMode = 'deterministic',
	interviewSessionId,
	autoStart = true,
	onDebugMessage,
	onTerminalMessage,
}: {
	chatId?: string;
	query: string | null;
	images?: ImageAttachment[];
	attachments?: AttachmentRef[];
	agentMode?: 'deterministic' | 'smart';
	interviewSessionId?: string | null;
	/**
	 * Whether a brand-new session may be created automatically on mount.
	 * In-app navigation (home prompt box, interview finish) sets this true.
	 * Sessions opened from an external/pasted /chat/new?query= link leave it
	 * false so the query — which seeds the agent's system prompt and debits
	 * Sparks — requires an explicit user gesture before it runs.
	 */
	autoStart?: boolean;
	onDebugMessage?: (type: 'error' | 'warning' | 'info' | 'websocket', message: string, details?: string, source?: string, messageType?: string, rawMessage?: unknown) => void;
	onTerminalMessage?: (log: { id: string; content: string; type: 'command' | 'stdout' | 'stderr' | 'info' | 'error' | 'warn' | 'debug'; timestamp: number; source?: string }) => void;
}) {
	const connectionStatus = useRef<'idle' | 'connecting' | 'connected' | 'failed' | 'retrying'>('idle');
	const retryCount = useRef(0);
	const maxRetries = 5;
	const retryTimeouts = useRef<NodeJS.Timeout[]>([]);
	// Track whether component is mounted and should attempt reconnects
	const shouldReconnectRef = useRef(true);
	// Track the latest connection attempt to avoid handling stale socket events
	const connectAttemptIdRef = useRef(0);
	const [chatId, setChatId] = useState<string>();
	// Set when agent creation is blocked by the free-tier usage gate (HTTP 429);
	// chat.tsx turns this into the connect/configure dialog when the flag is on.
	const [creationLimitExceeded, setCreationLimitExceeded] = useState(false);
	// Gate for externally-sourced new sessions: true while the init effect is
	// parked awaiting the user's explicit confirmation to start the build.
	const [awaitingStartConfirmation, setAwaitingStartConfirmation] = useState(false);
	const startConfirmedRef = useRef(false);
	// Fix-cycle narration state — persists across WS events so the "planning →
	// building → live, try again" story attaches to one card and closes cleanly.
	const fixCycleRef = useRef<{ id: string; active: boolean }>({ id: '', active: false });
	const [startTrigger, setStartTrigger] = useState(0);

	const confirmStart = useCallback(() => {
		startConfirmedRef.current = true;
		setAwaitingStartConfirmation(false);
		setStartTrigger((n) => n + 1);
	}, []);
	const [messages, setMessages] = useState<ChatMessage[]>([
		createAIMessage('main', 'Thinking...', true),
	]);

	const [bootstrapFiles, setBootstrapFiles] = useState<FileType[]>([]);
	const [blueprint, setBlueprint] = useState<BlueprintType>();
	const [previewUrl, setPreviewUrl] = useState<string>();
	const [query, setQuery] = useState<string>();

	const [websocket, setWebsocket] = useState<WebSocket>();

	const [isGeneratingBlueprint, setIsGeneratingBlueprint] = useState(false);
	const [isBootstrapping, setIsBootstrapping] = useState(true);

	const [projectStages, setProjectStages] = useState<ProjectStage[]>(defaultStages);

	// New state for phase timeline tracking
	const [phaseTimeline, setPhaseTimeline] = useState<PhaseTimelineItem[]>([]);

	const [files, setFiles] = useState<FileType[]>([]);

	const [totalFiles, setTotalFiles] = useState<number>();

	const [edit, setEdit] = useState<Omit<CodeFixEdits, 'type'>>();

	// Deployment and generation control state
	const [isDeploying, setIsDeploying] = useState(false);
	const [cloudflareDeploymentUrl, setCloudflareDeploymentUrl] = useState<string>('');
	const [deploymentError, setDeploymentError] = useState<string>();
	
	// Preview deployment state
	const [isPreviewDeploying, setIsPreviewDeploying] = useState(false);

	// Image generation state — drives the timeline "Generating images" indicator
	const [imageGeneration, setImageGeneration] = useState<ImageGenerationState>({
		active: false,
		completed: 0,
		total: 0,
	});

	// Redeployment state - tracks when redeploy button should be enabled
	const [isRedeployReady, setIsRedeployReady] = useState(false);
	// const [lastDeploymentPhaseCount, setLastDeploymentPhaseCount] = useState(0);
	const [isGenerationPaused, setIsGenerationPaused] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);

	// Phase progress visual indicator (used to apply subtle throb on chat)
	const [isPhaseProgressActive, setIsPhaseProgressActive] = useState(false);

	const [isThinking, setIsThinking] = useState(false);
	
	// Preview refresh state - triggers preview reload after deployment
	const [shouldRefreshPreview, setShouldRefreshPreview] = useState(false);
	
	// Track whether we've completed initial state restoration to avoid disrupting active sessions
	const [isInitialStateRestored, setIsInitialStateRestored] = useState(false);

	// Org collaboration: live presence roster, the current single-driver, and the
	// name of whoever a non-driver was just soft-blocked behind (for "take over").
	const [presenceMembers, setPresenceMembers] = useState<PresenceMember[]>([]);
	const [currentDriverId, setCurrentDriverId] = useState<string | null>(null);
	const [drivingBlockedBy, setDrivingBlockedBy] = useState<string | null>(null);
	// Consent-gated takeover: the inbound consent prompt (when THIS viewer is the
	// real user being taken over), the operator's own request status (when THIS
	// viewer is the operator awaiting consent), and the real user's local "I
	// consented; an operator is driving as me" flag (drives "Take back control").
	const [takeoverRequest, setTakeoverRequest] = useState<TakeoverRequest | null>(null);
	const [takeoverStatus, setTakeoverStatus] = useState<TakeoverStatus | null>(null);
	const [operatorHoldsGrant, setOperatorHoldsGrant] = useState(false);

	const updateStage = useCallback(
		(stageId: ProjectStage['id'], data: Partial<Omit<ProjectStage, 'id'>>) => {
			logger.debug('updateStage', { stageId, ...data });
			setProjectStages(prev => updateStageHelper(prev, stageId, data));
		},
		[],
	);

	const onCompleteBootstrap = useCallback(() => {
		updateStage('bootstrap', { status: 'completed' });
	}, [updateStage]);

	const clearEdit = useCallback(() => {
		setEdit(undefined);
	}, []);


	const sendMessage = useCallback((message: ChatMessage) => {
		// Only add conversational messages to the chat UI
		if (!isConversationalMessage(message.conversationId)) return;
		setMessages((prev: ChatMessage[]) => addOrUpdateMessage(prev, message));
	}, []);

	const sendUserMessage = useCallback((message: string) => {
		setMessages(prev => [...prev, createUserMessage(message)]);
	}, []);

	const loadBootstrapFiles = (files: FileType[]) => {
		setBootstrapFiles((prev) => [
			...prev,
			...files.map((file) => ({
				...file,
				language: getFileType(file.filePath),
			})),
		]);
	};

	// Create the WebSocket message handler
	const handleWebSocketMessage = useCallback(
		createWebSocketMessageHandler({
			// State setters
			setFiles,
			setPhaseTimeline,
			setProjectStages,
			setMessages,
			setBlueprint,
			setQuery,
			setPreviewUrl,
			setTotalFiles,
			setIsRedeployReady,
			setIsPreviewDeploying,
			setImageGeneration,
			setIsThinking,
			setIsInitialStateRestored,
			setShouldRefreshPreview,
			setIsDeploying,
			setCloudflareDeploymentUrl,
			setDeploymentError,
			setIsGenerationPaused,
			setIsGenerating,
			setIsPhaseProgressActive,
			setPresenceMembers,
			setCurrentDriverId,
			setDrivingBlockedBy,
			setTakeoverRequest,
			setTakeoverStatus,
			fixCycleRef,
			// Current state
			isInitialStateRestored,
			blueprint,
			query,
			bootstrapFiles,
			files,
			phaseTimeline,
			previewUrl,
			projectStages,
			isGenerating,
			urlChatId,
			// Functions
			updateStage,
			sendMessage,
			loadBootstrapFiles,
			onDebugMessage,
			onTerminalMessage,
		} as HandleMessageDeps),
		[
			isInitialStateRestored,
			blueprint,
			query,
			bootstrapFiles,
			files,
			phaseTimeline,
			previewUrl,
			projectStages,
			isGenerating,
			urlChatId,
			updateStage,
			sendMessage,
			loadBootstrapFiles,
			onDebugMessage,
			onTerminalMessage,
		]
	);

	// WebSocket connection with retry logic
	const connectWithRetry = useCallback(
		(
			wsUrl: string,
			{ disableGenerate = false, isRetry = false }: { disableGenerate?: boolean; isRetry?: boolean } = {},
		) => {
			logger.debug(`🔌 ${isRetry ? 'Retrying' : 'Attempting'} WebSocket connection (attempt ${retryCount.current + 1}/${maxRetries + 1}):`, wsUrl);
			
			if (!wsUrl) {
				logger.error('❌ WebSocket URL is required');
				return;
			}

			connectionStatus.current = isRetry ? 'retrying' : 'connecting';

			try {
				logger.debug('🔗 Attempting WebSocket connection to:', wsUrl);
				const ws = new WebSocket(wsUrl);
				setWebsocket(ws);

				// Mark this attempt id
				const myAttemptId = ++connectAttemptIdRef.current;

				// Connection timeout - if connection doesn't open within 30 seconds
				const connectionTimeout = setTimeout(() => {
					// Only handle timeout for the latest attempt
					if (myAttemptId !== connectAttemptIdRef.current) return;
					if (ws.readyState === WebSocket.CONNECTING) {
						logger.warn('⏰ WebSocket connection timeout');
						ws.close();
						handleConnectionFailure(wsUrl, disableGenerate, 'Connection timeout');
					}
				}, 30000);

				ws.addEventListener('open', () => {
					// Ignore stale open events
					if (!shouldReconnectRef.current) {
						ws.close();
						return;
					}
					if (myAttemptId !== connectAttemptIdRef.current) return;
					
					clearTimeout(connectionTimeout);
					logger.info('✅ WebSocket connection established successfully!');
					connectionStatus.current = 'connected';
					
					// Reset retry count on successful connection
					retryCount.current = 0;
					
					// Clear any pending retry timeouts
					retryTimeouts.current.forEach(clearTimeout);
					retryTimeouts.current = [];

					// Send success message to user
					if (isRetry) {
						sendMessage(createAIMessage('websocket_reconnected', 'Connection restored! Continuing with code generation...'));
					}

					// Always request conversation state explicitly (running/full history)
					sendWebSocketMessage(ws, 'get_conversation_state');

					// Request file generation for new chats only
					if (!disableGenerate && urlChatId === 'new') {
						logger.debug('🔄 Starting code generation for new chat');
						sendWebSocketMessage(ws, 'generate_all');
					}
				});

				ws.addEventListener('message', (event) => {
					try {
						const message: WebSocketMessage = JSON.parse(event.data);
						handleWebSocketMessage(ws, message);
					} catch (parseError) {
						logger.error('❌ Error parsing WebSocket message:', parseError, event.data);
					}
				});

				ws.addEventListener('error', (error) => {
					clearTimeout(connectionTimeout);
					// Only handle error for the latest attempt and when we should reconnect
					if (myAttemptId !== connectAttemptIdRef.current) return;
					if (!shouldReconnectRef.current) return;
					logger.error('❌ WebSocket error:', error);
					handleConnectionFailure(wsUrl, disableGenerate, 'WebSocket error');
				});

				ws.addEventListener('close', (event) => {
					clearTimeout(connectionTimeout);
					logger.info(
						`🔌 WebSocket connection closed with code ${event.code}: ${event.reason || 'No reason provided'}`,
						event,
					);
					// Only handle close for the latest attempt and when we should reconnect
					if (myAttemptId !== connectAttemptIdRef.current) return;
					if (!shouldReconnectRef.current) return;
					// Retry on any close while mounted (including 1000) to improve resilience
					handleConnectionFailure(wsUrl, disableGenerate, `Connection closed (code: ${event.code})`);
				});

				return function disconnect() {
					clearTimeout(connectionTimeout);
					ws.close();
				};
			} catch (error) {
				logger.error('❌ Error establishing WebSocket connection:', error);
				handleConnectionFailure(wsUrl, disableGenerate, 'Connection setup failed');
			}
		},
		[retryCount, maxRetries, retryTimeouts],
	);

	// Handle connection failures with exponential backoff retry
	const handleConnectionFailure = useCallback(
		(wsUrl: string, disableGenerate: boolean, reason: string) => {
			connectionStatus.current = 'failed';
			
			if (retryCount.current >= maxRetries) {
				logger.error(`💥 WebSocket connection failed permanently after ${maxRetries + 1} attempts`);
				sendMessage(createAIMessage('websocket_failed', `Connection failed permanently after ${maxRetries + 1} attempts.\n\nReason: ${reason}\n\nPlease refresh the page to try again.`));
				
				// Debug logging for permanent failure
				onDebugMessage?.('error',
					'WebSocket Connection Failed Permanently',
					`Failed after ${maxRetries + 1} attempts. Reason: ${reason}`,
					'WebSocket Resilience'
				);
				return;
			}

			retryCount.current++;
			
			// Exponential backoff: 2^attempt * 1000ms (1s, 2s, 4s, 8s, 16s)
			const retryDelay = Math.pow(2, retryCount.current) * 1000;
			const maxDelay = 30000; // Cap at 30 seconds
			const actualDelay = Math.min(retryDelay, maxDelay);

			logger.warn(`🔄 Retrying WebSocket connection in ${actualDelay / 1000}s (attempt ${retryCount.current + 1}/${maxRetries + 1})`);
			
			sendMessage(createAIMessage('websocket_retrying', `Connection failed. Retrying in ${Math.ceil(actualDelay / 1000)} seconds... (attempt ${retryCount.current + 1}/${maxRetries + 1})\n\nReason: ${reason}`, true));

			const timeoutId = setTimeout(() => {
				connectWithRetry(wsUrl, { disableGenerate, isRetry: true });
			}, actualDelay);
			
			retryTimeouts.current.push(timeoutId);
			
			// Debug logging for retry attempt
			onDebugMessage?.('warning',
				'WebSocket Connection Retry',
				`Retry ${retryCount.current}/${maxRetries} in ${actualDelay / 1000}s. Reason: ${reason}`,
				'WebSocket Resilience'
			);
		},
		[maxRetries, retryCount, retryTimeouts, onDebugMessage, sendMessage],
	);

    // No legacy wrapper; call connectWithRetry directly

	useEffect(() => {
		async function init() {
			if (!urlChatId || connectionStatus.current !== 'idle') return;

			try {
				if (urlChatId === 'new') {
					if (!userQuery) {
						const errorMsg = 'Please enter a description of what you want to build';
						logger.error('Query is required for new code generation');
						toast.error(errorMsg);
						return;
					}

					// Gate externally-sourced sessions: the query seeds the agent's
					// system prompt and debits Sparks, so require an explicit user
					// gesture when the session was not started from in-app navigation.
					if (!autoStart && !startConfirmedRef.current) {
						setAwaitingStartConfirmation(true);
						return;
					}

					// Prevent duplicate session creation if the effect re-runs
					// while creation is still streaming.
					connectionStatus.current = 'connecting';

					// Start new code generation using API client
					const response = await apiClient.createAgentSession({
						query: userQuery,
						agentMode,
						images: userImages, // Pass images from URL params for multi-modal blueprint
						attachments: userAttachments, // Already-uploaded file refs (server re-verifies ownership)
						interviewSessionId: interviewSessionId ?? undefined,
					});

					const parser = createRepairingJSONParser();

					const result: {
						websocketUrl: string;
						agentId: string;
						template: {
							files: FileType[];
						};
					} = {
						websocketUrl: '',
						agentId: '',
						template: {
							files: [],
						},
					};

					let startedBlueprintStream = false;
					let creationFailed = false;
					sendMessage(createAIMessage('main', "Sure, let's get started. Bootstrapping the project first...", true));

					for await (const obj of ndjsonStream(response.stream)) {
                        logger.debug('Received chunk from server:', obj);
						// Server-reported initialization failure (e.g. the blueprint
						// model returned 429). Without this branch the error line was
						// silently ignored and the UI froze at "Blueprint is being
						// generated..." while the WS layer reconnect-looped forever.
						if (obj.error) {
							creationFailed = true;
							logger.error('Code generation failed to start:', obj.error);
							setIsBootstrapping(false);
							setIsGeneratingBlueprint(false);
							updateStage('blueprint', { status: 'error' });
							sendMessage(createAIMessage('main', `Build failed to start: ${String(obj.error)}. If you brought your own LLM key, check its rate limits/billing — otherwise please try again.`));
							toast.error(String(obj.error));
							continue;
						}
						if (obj.chunk) {
							if (!startedBlueprintStream) {
								sendMessage(createAIMessage('main', 'Blueprint is being generated...', true));
								logger.info('Blueprint stream has started');
								setIsBootstrapping(false);
								setIsGeneratingBlueprint(true);
								startedBlueprintStream = true;
								updateStage('bootstrap', { status: 'completed' });
								updateStage('blueprint', { status: 'active' });
							}
							parser.feed(obj.chunk);
							try {
								const partial = parser.finalize();
								setBlueprint(partial);
							} catch {
								// A partial streamed chunk is often unrepairable until
								// more data arrives — the next chunk retries, and the
								// post-stream finalize below is the authoritative parse.
								logger.debug('Blueprint chunk not yet parseable, awaiting more data');
							}
						} 
						if (obj.agentId) {
							result.agentId = obj.agentId;
						}
						if (obj.websocketUrl) {
							result.websocketUrl = obj.websocketUrl;
							logger.debug('📡 Received WebSocket URL from server:', result.websocketUrl)
						}
						if (obj.template) {
                            logger.debug('Received template from server:', obj.template);
							result.template = obj.template;
							if (obj.template.files) {
								loadBootstrapFiles(obj.template.files);
							}
						}
					}

					// Refresh the Sparks balance — the build debit happened server-side
					// at agent creation, whether or not the build then succeeded.
					window.dispatchEvent(new Event('billing-updated'));

					if (creationFailed) {
						// Do NOT connect the WebSocket or mark stages complete —
						// the agent never initialized.
						return;
					}

					if (startedBlueprintStream) {
						try {
							setBlueprint(parser.finalize());
						} catch (e) {
							logger.error('Error parsing blueprint after stream completion:', e);
						}
					}

					updateStage('blueprint', { status: 'completed' });
					setIsGeneratingBlueprint(false);
					sendMessage(createAIMessage('main', 'Blueprint generation complete. Now starting the code generation...', true));

					// Connect to WebSocket
					logger.debug('connecting to ws with created id');
					connectWithRetry(result.websocketUrl);
					setChatId(result.agentId); // This comes from the server response
					
					// Emit app-created event for sidebar updates
					appEvents.emitAppCreated(result.agentId, {
						title: userQuery || 'New App',
						description: userQuery,
					});
				} else if (connectionStatus.current === 'idle') {
					setIsBootstrapping(false);
					// Get existing progress
					sendMessage(createAIMessage('fetching-chat', 'Fetching your previous chat...'));

					// Fetch existing agent connection details
					const response = await apiClient.connectToAgent(urlChatId);
					if (!response.success || !response.data) {
						logger.error('Failed to fetch existing chat:', { chatId: urlChatId, error: response.error });
						throw new Error(response.error?.message || 'Failed to connect to agent');
					}

					logger.debug('Existing agentId API result', response.data);
					// Set the chatId for existing chat - this enables the chat input
					setChatId(urlChatId);

					sendMessage(createAIMessage('resuming-chat', 'Starting from where you left off...'));

					logger.debug('connecting from init for existing chatId');
					connectWithRetry(response.data.websocketUrl, {
						disableGenerate: true, // We'll handle generation resume in the WebSocket open handler
					});
				}
			} catch (error) {
				logger.error('Error initializing code generation:', error);
				if (error instanceof RateLimitExceededError) {
					const rateLimitMessage = handleRateLimitError(error.details, onDebugMessage);
					setMessages(prev => [...prev, rateLimitMessage]);
				} else if (error instanceof ApiError) {
					// requestRaw leaves bare ApiErrors un-toasted, so surface it here.
					// A 429 means the free build tier is exhausted at creation → raise the
					// signal that chat.tsx renders as the connect/configure CTA.
					toast.error(error.message);
					if (error.status === 429) {
						setCreationLimitExceeded(true);
					}
				}
			}
		}
		init();
		// startTrigger re-arms init exactly once: the gated first run returned
		// before touching connectionStatus, and confirmStart bumps the trigger.
	}, [startTrigger]);

    // Mount/unmount: enable/disable reconnection and clear pending retries
    useEffect(() => {
        shouldReconnectRef.current = true;
        return () => {
            shouldReconnectRef.current = false;
            retryTimeouts.current.forEach(clearTimeout);
            retryTimeouts.current = [];
        };
    }, []);

    // Close previous websocket on change
    useEffect(() => {
        return () => {
            websocket?.close();
        };
    }, [websocket]);

	useEffect(() => {
		if (edit) {
			// When edit is cleared, write the edit changes
			return () => {
				setFiles((prev) =>
					prev.map((file) => {
						if (file.filePath === edit.filePath) {
							file.fileContents = file.fileContents.replace(
								edit.search,
								edit.replacement,
							);
						}
						return file;
					}),
				);
			};
		}
	}, [edit]);

	// Control functions for deployment and generation
	const handleStopGeneration = useCallback(() => {
		sendWebSocketMessage(websocket, 'stop_generation');
	}, [websocket]);

	const handleResumeGeneration = useCallback(() => {
		sendWebSocketMessage(websocket, 'resume_generation');
	}, [websocket]);

	const handleDeployToCloudflare = useCallback(async (instanceId: string) => {
		try {
			// Send deployment command via WebSocket instead of HTTP request
			if (sendWebSocketMessage(websocket, 'deploy', { instanceId })) {
				logger.debug('🚀 Deployment WebSocket message sent:', instanceId);
				
				// Set 1-minute timeout for deployment
				setTimeout(() => {
					if (isDeploying) {
						logger.warn('⏰ Deployment timeout after 1 minute');
						
						// Reset deployment state
						setIsDeploying(false);
						setCloudflareDeploymentUrl('');
						setIsRedeployReady(false);
						
						// Show timeout message
						sendMessage(createAIMessage('deployment_timeout', `Deployment timed out after 1 minute.\n\nPlease try deploying again. The server may be busy.`));
						
						// Debug logging for timeout
						onDebugMessage?.('warning', 
							'Deployment Timeout',
							`Deployment for ${instanceId} timed out after 60 seconds`,
							'Deployment Timeout Management'
						);
					}
				}, 60000); // 1 minute = 60,000ms
				
				// Store timeout ID for cleanup if deployment completes early
				// Note: In a real implementation, you'd want to clear this timeout
				// when deployment completes successfully
				
			} else {
				throw new Error('WebSocket connection not available');
			}
		} catch (error) {
			logger.error('❌ Error sending deployment WebSocket message:', error);
			
			// Set deployment state immediately for UI feedback
			setIsDeploying(true);
			// Clear any previous deployment error
			setDeploymentError('');
			setCloudflareDeploymentUrl('');
			setIsRedeployReady(false);
			
			sendMessage(createAIMessage('deployment_error', `Failed to initiate deployment: ${error instanceof Error ? error.message : 'Unknown error'}\n\nYou can try again.`));
		}
	}, [websocket, sendMessage, isDeploying, onDebugMessage]);

	// Org collaboration: claim (take over) / release the single driver seat. For
	// the real user this is also "take back control", which clears the local grant
	// flag (the server-side revoke happens via claim_driver).
	const claimDriving = useCallback(() => {
		setDrivingBlockedBy(null);
		setOperatorHoldsGrant(false);
		sendWebSocketMessage(websocket, 'claim_driver');
	}, [websocket]);

	const releaseDriving = useCallback(() => {
		sendWebSocketMessage(websocket, 'release_driver');
	}, [websocket]);

	// Consent-gated takeover: the real user's allow/deny answer. Closes the prompt
	// immediately; on allow, remember locally that an operator now drives as us so
	// "Take back control" is offered.
	const respondToTakeover = useCallback(
		(requestId: string, allow: boolean) => {
			sendWebSocketMessage(websocket, 'takeover_decision', { requestId, allow });
			setTakeoverRequest(null);
			setOperatorHoldsGrant(allow);
		},
		[websocket],
	);

	const dismissTakeoverStatus = useCallback(() => setTakeoverStatus(null), []);

	// In-editor manual code edit: persist a generated file the user edited. The
	// server commits it as a git reversion point, redeploys the preview (HMR), and
	// broadcasts file_regenerated so every member's file view syncs.
	const saveFileEdit = useCallback(
		(filePath: string, fileContents: string) => {
			sendWebSocketMessage(websocket, 'user_edit_file', { filePath, fileContents });
		},
		[websocket],
	);

	return {
		messages,
		edit,
		bootstrapFiles,
		chatId,
		query,
		files,
		blueprint,
		previewUrl,
		isGeneratingBlueprint,
		isBootstrapping,
		totalFiles,
		websocket,
		sendUserMessage,
		sendAiMessage: sendMessage,
		clearEdit,
		projectStages,
		phaseTimeline,
		isThinking,
		onCompleteBootstrap,
		// Deployment and generation control
		isDeploying,
		cloudflareDeploymentUrl,
		deploymentError,
		isRedeployReady,
		isGenerationPaused,
		isGenerating,
		handleStopGeneration,
		handleResumeGeneration,
		handleDeployToCloudflare,
		// Preview refresh control
		shouldRefreshPreview,
		// Preview deployment state
		isPreviewDeploying,
		// Image generation progress
		imageGeneration,
		// Phase progress visual indicator
		isPhaseProgressActive,
		// Org collaboration: presence + single-driver seat
		presenceMembers,
		currentDriverId,
		drivingBlockedBy,
		setDrivingBlockedBy,
		claimDriving,
		// Consent-gated takeover
		takeoverRequest,
		takeoverStatus,
		operatorHoldsGrant,
		respondToTakeover,
		dismissTakeoverStatus,
		// In-editor code editing
		saveFileEdit,
		releaseDriving,
		// CF-OAuth: free build tier exhausted at creation (HTTP 429)
		creationLimitExceeded,
		clearCreationLimit: () => setCreationLimitExceeded(false),
		// Externally-sourced session start gate
		awaitingStartConfirmation,
		confirmStart,
	};
}
